// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

// Locale-aware DISPLAY formatting. Stored values (integer minor units, ISO-8601
// timestamps) never change — only how they render. The active display locale is
// held here as a module value so the many existing `formatAmount(...)` call
// sites become locale-aware without threading a locale argument through every
// component; i18n/config.ts keeps it in sync with the active language, and
// components re-render on language change via useTranslation/useFormat. Tests and
// callers that need determinism can always pass an explicit `locale`.

let currentDisplayLocale = 'en-US';

/** Update the locale used by the argument-less formatters (called by i18n). */
export function setDisplayLocale(locale: string): void {
  currentDisplayLocale = locale || 'en-US';
}

export function getDisplayLocale(): string {
  return currentDisplayLocale;
}

/**
 * Localized currency name via Intl.DisplayNames (e.g. "US Dollar" → "Dólar
 * estadounidense" in es). Falls back to the given fallback (the bundled English
 * name) or the code itself when the runtime can't name it.
 */
export function getCurrencyName(code: string, fallback?: string, locale = currentDisplayLocale): string {
  try {
    const name = new Intl.DisplayNames([locale], { type: 'currency' }).of(code);
    if (name && name !== code) return name;
  } catch {
    // Intl.DisplayNames unsupported or bad code — fall through.
  }
  return fallback ?? code;
}

/** Format a plain number for display (grouping/decimal separators per locale). */
export function formatNumber(
  value: number,
  options?: Intl.NumberFormatOptions,
  locale = currentDisplayLocale,
): string {
  try {
    return new Intl.NumberFormat(locale, options).format(value);
  } catch {
    return String(value);
  }
}

/**
 * Format a date for display. Accepts an ISO string ("2026-07-13"), a full
 * timestamp, or a Date. Date-only strings are pinned to local midnight so the
 * calendar day never shifts across time zones (matches the existing
 * `new Date(d + 'T00:00:00')` idiom used throughout the app).
 */
export function formatDate(
  input: string | Date,
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' },
  locale = currentDisplayLocale,
): string {
  const date =
    input instanceof Date
      ? input
      : /^\d{4}-\d{2}-\d{2}$/.test(input)
        ? new Date(input + 'T00:00:00')
        : new Date(input);
  if (isNaN(date.getTime())) return typeof input === 'string' ? input : '';
  try {
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}
